import { Typography } from "@mui/material";
import ErrorModalImage from "assets/illustrations/errorModalImage.svg";
import { ModalContentWrapper } from "components/primitives/ModalContentWrapper";
import { ModalTitle } from "components/primitives/ModalTitle";
import { useCountdown } from "hooks/timers";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFridgeId } from "utils/browser/localStorage";
import { logToSentry } from "utils/logger";
import { getShortenedFridgeId } from "utils/utils";
import { BasicModal } from "./BasicModal";
import { ModalSupportFooter } from "./ModalSupportFooter";

type ConnectionErrorModalProps = {
	appConnected: boolean;
	isSubscriptionBroken?: boolean;
};

export const ConnectionErrorModal = ({ appConnected, isSubscriptionBroken }: ConnectionErrorModalProps) => {
	const fridgeId = getFridgeId();
	const shortenedFridgeId = getShortenedFridgeId(fridgeId);
	const [isOpen, setIsOpen] = useState(
		isSubscriptionBroken !== undefined && isSubscriptionBroken ? false : !appConnected,
	);
	const { countdown } = useCountdown(15);
	const { trackTabletErrorModal } = useMixpanelTrackers();
	const trackRef = useRef(trackTabletErrorModal);
	trackRef.current = trackTabletErrorModal;
	const wasOpenRef = useRef(false);

	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			wasOpenRef.current = true;
			trackRef.current("open");
			logToSentry(`Error modal displayed on fridge: ${fridgeId}`, "error");
		}
		return () => {
			if (wasOpenRef.current) {
				wasOpenRef.current = false;
				trackRef.current("close");
			}
		};
	}, [isOpen, fridgeId]);

	// Keep the modal in sync with the connection, both directions. Before 1.14.0 the modal rendered
	// `open={!appConnected}`, so when the Google-DNS ping flipped appConnected back to true the modal
	// closed itself. The JID-1131 refactor replaced that with a sticky isOpen that was only ever set
	// to true, so the modal could no longer dismiss on recovery — it relied on being unmounted. Restore
	// the reactive close: hide the moment we reconnect; otherwise show (after the grace countdown when
	// the trigger is a dropped subscription rather than a hard disconnect).
	useEffect(() => {
		if (appConnected) {
			setIsOpen(false);
		} else if (!isSubscriptionBroken || countdown === 0) {
			setIsOpen(true);
		}
	}, [appConnected, isSubscriptionBroken, countdown]);

	return (
		<BasicModal
			open={isOpen}
			fullHeight={true}
			closable={false}
			modalContent={<ErrorModalContent fridgeId={shortenedFridgeId} />}
		/>
	);
};

const ErrorModalContent = ({ fridgeId }: { fridgeId: string }) => {
	const { t } = useTranslation();
	return (
		<ModalContentWrapper>
			<ModalTitle variant="promoHeader">{t("modals.error.title")}</ModalTitle>
			<Typography variant="promoText" mb={5}>
				{t("modals.error.body")}
			</Typography>
			<ErrorModalImage />
			<ModalSupportFooter fridgeId={fridgeId} />
		</ModalContentWrapper>
	);
};
